import { coreApi, patch, post, remove } from '../coreApi';

export interface PlatformPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface PaginatedResult<T> {
  rows: T[];
  pagination: PlatformPagination;
}

export interface PlatformOverview {
  totalTalent: number;
  pendingTalent: number;
  activeTalent: number;
  suspendedTalent: number;
  enabledInstances: number;
  activeWorkRequests: number;
  overdueWorkRequests: number;
  failedNotifications: number;
  openSecurityIssues: number;
  outstandingPayoutRecords: number;
}

export interface PlatformMember {
  id: number;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  memberType?: string | null;
  profileType?: string | null;
  providerKey?: string | null;
  profileImageUrl?: string | null;
  bannerImageUrl?: string | null;
  bannerFocalPointJson?: {
    x: number;
    y: number;
  } | null;
  networkTypesJson?: string[];
  timezone?: string | null;
  country?: string | null;
  globalStatus: string;
  verificationStatus?: string;
  portalStatus?: string;
  portalVerifiedAt?: string | null;
  portalLastLoginAt?: string | null;
  portalLockedUntil?: string | null;
  portalLastPasswordResetAt?: string | null;
  portalTemporaryPasswordExpiresAt?: string | null;
  portalPasswordChangeRequired?: boolean | null;
  portalLastPasswordResetByUserId?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  membershipCount?: number;
  activeMembershipCount?: number;
  capabilityTypes?: string[];
  sourceType?: string | null;
  sourceId?: string | number | null;
}

export interface CreatePlatformMemberInput {
  displayName: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  memberType: string;
  profileType?: string | null;
  providerKey?: string | null;
  profileImageUrl?: string | null;
  networkTypesJson?: string[];
  capabilityTypes?: string[];
  timezone?: string | null;
  country?: string | null;
  globalStatus?: string;
  reason?: string | null;
}

export interface PlatformMembership {
  id: number;
  memberId: number;
  instanceId: number;
  instanceUniqueId?: string | null;
  instanceName?: string | null;
  status: string;
  role?: string | null;
  visibility?: string | null;
  allowedRequestTypesJson?: string[] | null;
  rateJson?: {
    type?: string | null;
    amount?: number | string | null;
    currency?: string | null;
  } | null;
  isEliteCoreNetworkDecorator?: boolean;
  approvedByUserId?: number | null;
  createdByUserId?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  member?: Pick<PlatformMember, 'id' | 'displayName' | 'companyName' | 'globalStatus' | 'profileImageUrl'> | null;
}

export interface PlatformCapabilityMetadata {
  turnaround?: {
    value?: number | null;
    unit?: 'business_hours' | 'business_days' | 'calendar_days' | null;
    rushAvailable?: boolean;
    rushValue?: number | null;
    minimumLeadTime?: number | null;
  };
  pricing?: {
    enabled?: boolean;
    rateType?: string | null;
    amount?: number | null;
    currency?: string | null;
    minimumCharge?: number | null;
  };
  capacity?: {
    maximumActiveJobs?: number | null;
    minimumQuantity?: number | null;
    maximumQuantity?: number | null;
    maximumOrderVolume?: number | null;
    hourlyCapacity?: number | null;
  };
  acceptedFileTypes?: string[];
  outputFileTypes?: string[];
  decorationMethods?: string[];
  serviceOptions?: string[];
  taskTypes?: string[];
  productionLocation?: string | null;
  softwareExperience?: string | null;
  colourMatchingSupported?: boolean;
  complexArtworkSupported?: boolean;
  sampleProductionAvailable?: boolean;
}

export interface PlatformCapability {
  id: number;
  memberId: number;
  membershipId?: number | null;
  instanceId?: number | null;
  capabilityType: string;
  capabilityGroup?: string | null;
  displayName?: string | null;
  description?: string | null;
  metadataJson?: PlatformCapabilityMetadata | null;
  isActive?: boolean;
  notes?: string | null;
  source?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CreatePlatformCapabilityInput {
  capabilityType: string;
  capabilityGroup?: string | null;
  displayName?: string | null;
  description?: string | null;
  metadataJson?: PlatformCapabilityMetadata | null;
  isActive?: boolean;
  notes?: string | null;
}

export interface UpdatePlatformCapabilityInput {
  capabilityType?: string;
  capabilityGroup?: string | null;
  displayName?: string | null;
  description?: string | null;
  metadataJson?: PlatformCapabilityMetadata | null;
  isActive?: boolean;
  notes?: string | null;
}

export interface CreatePlatformMembershipInput {
  memberId: number;
  status: string;
  role?: string | null;
  visibility?: string | null;
  allowedRequestTypesJson?: string[];
  rateJson?: {
    type?: string | null;
    amount?: number | null;
    currency?: string | null;
  } | null;
  reason?: string | null;
}

export interface PlatformInstance {
  id: number;
  instanceUniqueId?: string | null;
  instanceName?: string | null;
  activeStatus?: string | null;
  mvpTalentEnabled: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  membershipCount?: number;
  requestCount?: number;
}

export interface PlatformAssignmentSummary {
  id: number;
  status: string;
  memberId?: number | null;
  membershipId?: number | null;
  providerStatus?: string | null;
  member?: Pick<PlatformMember, 'id' | 'displayName' | 'companyName' | 'profileImageUrl'> | null;
}

export interface PlatformWorkRequest {
  id: number;
  instanceId: number;
  instanceUniqueId?: string | null;
  instanceName?: string | null;
  orderId?: string | number | null;
  poId?: string | number | null;
  productionJobId?: string | number | null;
  documentType?: string | null;
  documentId?: string | number | null;
  requestType: string;
  sourceModule?: string | null;
  fulfillmentProvider: string;
  networkType?: string | null;
  status: string;
  rawStatus?: string | null;
  providerStatusSummary?: string | null;
  title?: string | null;
  description?: string | null;
  instructions?: string | null;
  deadlineAt?: string | null;
  priority?: string | null;
  revisionCount?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  submittedAt?: string | null;
  completedAt?: string | null;
  lastActivityAt?: string | null;
  createdByUser?: { id: number; first_name?: string; last_name?: string; email?: string } | null;
  assignment?: PlatformAssignmentSummary | null;
}

export interface PlatformNotificationDelivery {
  id: number;
  instanceId?: number | null;
  workRequestId?: number | null;
  assignmentId?: number | null;
  offerId?: number | null;
  messageId?: number | null;
  submissionId?: number | null;
  notificationType: string;
  recipientType: string;
  recipientUserId?: number | null;
  recipientMemberId?: number | null;
  channel: string;
  recipientEmail?: string | null;
  subject?: string | null;
  status: string;
  providerMessageId?: string | null;
  errorMessage?: string | null;
  sentAt?: string | null;
  failedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  metadataJson?: Record<string, unknown> | null;
}

export interface PlatformPortalToken {
  id: number;
  memberId: number;
  instanceId?: number | null;
  tokenType: string;
  createdAt?: string | null;
  expiresAt?: string | null;
  consumedAt?: string | null;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  sentToEmail?: string | null;
  metadataJson?: Record<string, unknown> | null;
}

export interface PlatformPortalAccessResult {
  member: PlatformMember;
  portalToken?: PlatformPortalToken | null;
  notificationDelivery?: PlatformNotificationDelivery | null;
  portalUrl?: string | null;
  portalLoginUrl?: string | null;
  portalDashboardUrl?: string | null;
  invitationUrl?: string | null;
  warning?: string | null;
}

export interface PortalAccessMutationInput {
  reason: string;
  sendInvitation?: boolean;
}

export interface TalentPasswordResetInput {
  reason: string;
  sendEmail?: boolean;
  bccAdministrator?: boolean;
}

export interface TalentTemporaryPasswordInput {
  reason: string;
  sendEmail?: boolean;
  bccAdministrator?: boolean;
}

export interface SetTalentTemporaryPasswordInput extends TalentTemporaryPasswordInput {
  password: string;
  confirmPassword: string;
  requirePasswordChange?: boolean;
}

export interface TalentPasswordManagementResult {
  success?: true;
  member?: PlatformMember;
  passwordResetStatus?: string | null;
  notificationDelivery?: PlatformNotificationDelivery | null;
  mustChangePassword?: boolean;
  expiresAt?: string | null;
  sessionsRevoked?: number;
}

export interface TalentTemporaryPasswordResult extends TalentPasswordManagementResult {
  temporaryPassword?: string;
}

export interface RevokeAllSessionsResult {
  memberId: number;
  revokedSessionCount: number;
}

export interface PlatformAuditEvent {
  id: number;
  actorUserId: number;
  action: string;
  targetType: string;
  targetId?: string | null;
  instanceId?: number | null;
  reason?: string | null;
  previousValuesJson?: Record<string, unknown> | null;
  newValuesJson?: Record<string, unknown> | null;
  metadataJson?: Record<string, unknown> | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt?: string | null;
  actorUser?: { id: number; first_name?: string; last_name?: string; email?: string } | null;
}

export interface PlatformSettings {
  coreNetworkGlobalEnabled: boolean;
  portalAccessMode: 'enabled' | 'read_only' | 'disabled';
}

export interface PlatformRequestOptions {
  signal?: AbortSignal;
}

export const serializePlatformFilters = (filters: Record<string, unknown>): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== '') {
          params.append(key, String(entry));
        }
      });
      return;
    }
    params.set(key, String(value));
  });
  return params;
};

type PlatformSuccessEnvelope<T extends object> = { success: true } & T;

export const unwrapPlatformEnvelope = <T extends Record<string, unknown>, K extends keyof T>(
  response: PlatformSuccessEnvelope<T>,
  field: K
): T[K] => {
  if (!response || response.success !== true) {
    throw new Error('Invalid platform API response envelope.');
  }

  return response[field];
};

export const unwrapPaginatedPlatformEnvelope = <T>(
  response: PlatformSuccessEnvelope<PaginatedResult<T> & Record<string, unknown>>
): PaginatedResult<T> => {
  if (!response || response.success !== true || !Array.isArray(response.rows) || !response.pagination) {
    throw new Error('Invalid platform API pagination response.');
  }

  return {
    rows: response.rows,
    pagination: response.pagination,
  };
};

const request = async <T>(config: { method: 'get' | 'post' | 'patch'; url: string; params?: Record<string, unknown>; data?: unknown; signal?: AbortSignal }): Promise<T> => {
  const response = await coreApi.request<T>({
    method: config.method,
    url: config.url,
    params: config.params,
    data: config.data,
    signal: config.signal,
  });
  return response.data;
};

export const platformCoreNetworkApi = {
  getOverview: async (options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ overview: PlatformOverview }>>({ method: 'get', url: '/api/platform/core-network/overview', signal: options.signal });
    return unwrapPlatformEnvelope(response, 'overview') as PlatformOverview;
  },
  listMembers: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformMember>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformMember> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/members', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  createMember: async (data: CreatePlatformMemberInput) => {
    const response = await post<{ success: true; member: PlatformMember }>('/api/platform/core-network/members', data);
    return response.member;
  },
  getMember: async (memberId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ member: PlatformMember }>>({ method: 'get', url: `/api/platform/core-network/members/${memberId}`, signal: options.signal });
    return unwrapPlatformEnvelope(response, 'member') as PlatformMember;
  },
  uploadMemberProfileImage: async (memberId: number | string, file: File | Blob) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await post<{ success: true; member: PlatformMember }>(
      `/api/platform/core-network/members/${memberId}/profile-image`,
      formData
    );
    return response.member;
  },
  removeMemberProfileImage: async (memberId: number | string) => {
    const response = await remove<{ success: true; member: PlatformMember }>(
      `/api/platform/core-network/members/${memberId}/profile-image`
    );
    return response.member;
  },
  uploadMemberBannerImage: async (
    memberId: number | string,
    file: File | Blob,
    focalPoint?: { x: number; y: number } | null
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (focalPoint) {
      formData.append('focalPointJson', JSON.stringify(focalPoint));
    }
    const response = await post<{ success: true; member: PlatformMember }>(
      `/api/platform/core-network/members/${memberId}/banner-image`,
      formData
    );
    return response.member;
  },
  removeMemberBannerImage: async (memberId: number | string) => {
    const response = await remove<{ success: true; member: PlatformMember }>(
      `/api/platform/core-network/members/${memberId}/banner-image`
    );
    return response.member;
  },
  updateMemberStatus: async (memberId: number | string, data: { status: string; reason: string }) => {
    const response = await patch<{ success: true; member: PlatformMember }>(`/api/platform/core-network/members/${memberId}/status`, data);
    return response.member;
  },
  updateMemberVerification: async (memberId: number | string, data: { verified?: boolean; accountStatus?: string; reason: string }) => {
    const response = await patch<{ success: true; member: PlatformMember }>(`/api/platform/core-network/members/${memberId}/verification`, data);
    return response.member;
  },
  getMemberMemberships: async (memberId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ memberships: PlatformMembership[] }>>({ method: 'get', url: `/api/platform/core-network/members/${memberId}/memberships`, signal: options.signal });
    return unwrapPlatformEnvelope(response, 'memberships') as PlatformMembership[];
  },
  listMemberCapabilities: async (memberId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ capabilities: PlatformCapability[] }>>({
      method: 'get',
      url: `/api/platform/core-network/members/${memberId}/capabilities`,
      signal: options.signal,
    });
    return unwrapPlatformEnvelope(response, 'capabilities') as PlatformCapability[];
  },
  createMemberCapability: async (memberId: number | string, data: CreatePlatformCapabilityInput) => {
    const response = await post<{ success: true; capability: PlatformCapability }>(
      `/api/platform/core-network/members/${memberId}/capabilities`,
      data
    );
    return response.capability;
  },
  updateMemberCapability: async (memberId: number | string, capabilityId: number | string, data: UpdatePlatformCapabilityInput) => {
    const response = await patch<{ success: true; capability: PlatformCapability }>(
      `/api/platform/core-network/members/${memberId}/capabilities/${capabilityId}`,
      data
    );
    return response.capability;
  },
  getMemberActivity: async (memberId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ activity: Record<string, unknown> }>>({ method: 'get', url: `/api/platform/core-network/members/${memberId}/activity`, signal: options.signal });
    return unwrapPlatformEnvelope(response, 'activity') as Record<string, unknown>;
  },
  enableMemberPortalAccess: async (memberId: number | string, data: PortalAccessMutationInput): Promise<PlatformPortalAccessResult> => {
    const response = await post<PlatformSuccessEnvelope<PlatformPortalAccessResult>>(`/api/platform/core-network/members/${memberId}/enable-portal-access`, data);
    return response;
  },
  resendMemberPortalInvitation: async (memberId: number | string, data: PortalAccessMutationInput): Promise<PlatformPortalAccessResult> => {
    const response = await post<PlatformSuccessEnvelope<PlatformPortalAccessResult>>(`/api/platform/core-network/members/${memberId}/resend-portal-invitation`, data);
    return response;
  },
  restoreMemberPortalAccess: async (memberId: number | string, data: PortalAccessMutationInput): Promise<PlatformPortalAccessResult> => {
    const response = await post<PlatformSuccessEnvelope<PlatformPortalAccessResult>>(`/api/platform/core-network/members/${memberId}/restore-portal-access`, data);
    return response;
  },
  unlockMemberPortalAccess: async (memberId: number | string, reason: string): Promise<PlatformPortalAccessResult> => {
    const response = await post<PlatformSuccessEnvelope<PlatformPortalAccessResult>>(`/api/platform/core-network/members/${memberId}/unlock-portal-access`, { reason });
    return response;
  },
  revokePortalAccess: async (memberId: number | string, reason: string) => {
    const response = await post<{ success: true; member: PlatformMember }>(`/api/platform/core-network/members/${memberId}/revoke-portal-access`, { reason });
    return response.member;
  },
  listInstances: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformInstance>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformInstance> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/instances', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  getInstance: async (instanceId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ instance: PlatformInstance }>>({ method: 'get', url: `/api/platform/core-network/instances/${instanceId}`, signal: options.signal });
    return unwrapPlatformEnvelope(response, 'instance') as PlatformInstance;
  },
  updateInstanceSettings: async (instanceId: number | string, data: { mvpTalentEnabled: boolean; reason: string }) => {
    const response = await patch<{ success: true; instance: PlatformInstance }>(`/api/platform/core-network/instances/${instanceId}/settings`, data);
    return response.instance;
  },
  getInstanceMemberships: async (instanceId: number | string, filters: Record<string, unknown> = {}, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ memberships: PlatformMembership[] }>>({ method: 'get', url: `/api/platform/core-network/instances/${instanceId}/memberships`, params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPlatformEnvelope(response, 'memberships') as PlatformMembership[];
  },
  createInstanceMembership: async (instanceId: number | string, data: CreatePlatformMembershipInput) => {
    const response = await post<{ success: true; membership: PlatformMembership }>(`/api/platform/core-network/instances/${instanceId}/memberships`, data);
    return response.membership;
  },
  updateInstanceMembership: async (instanceId: number | string, membershipId: number | string, data: Partial<CreatePlatformMembershipInput>) => {
    const response = await patch<{ success: true; membership: PlatformMembership }>(`/api/platform/core-network/instances/${instanceId}/memberships/${membershipId}`, data);
    return response.membership;
  },
  listWorkRequests: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformWorkRequest>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformWorkRequest> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/work-requests', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  getWorkRequest: async (requestId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ workRequest: PlatformWorkRequest }>>({ method: 'get', url: `/api/platform/core-network/work-requests/${requestId}`, signal: options.signal });
    return unwrapPlatformEnvelope(response, 'workRequest') as PlatformWorkRequest;
  },
  cancelWorkRequest: async (requestId: number | string, reason: string) => {
    const response = await post<{ success: true; workRequest: PlatformWorkRequest }>(`/api/platform/core-network/work-requests/${requestId}/cancel`, { reason });
    return response.workRequest;
  },
  reassignWorkRequest: async (requestId: number | string, data: Record<string, unknown>) => {
    return post<{ success: true; workRequest: PlatformWorkRequest; assignment: PlatformAssignmentSummary }>(`/api/platform/core-network/work-requests/${requestId}/reassign`, data);
  },
  addSupportNote: async (requestId: number | string, data: { messageBody: string; assignmentId?: number | string; reason?: string }) => {
    const response = await post<{ success: true; note: Record<string, unknown> }>(`/api/platform/core-network/work-requests/${requestId}/support-note`, data);
    return response.note;
  },
  listNotificationDeliveries: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformNotificationDelivery>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformNotificationDelivery> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/notification-deliveries', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  getNotificationDelivery: async (deliveryId: number | string, options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ delivery: PlatformNotificationDelivery }>>({ method: 'get', url: `/api/platform/core-network/notification-deliveries/${deliveryId}`, signal: options.signal });
    return unwrapPlatformEnvelope(response, 'delivery') as PlatformNotificationDelivery;
  },
  retryNotificationDelivery: async (deliveryId: number | string, reason: string) => {
    const response = await post<{ success: true; delivery: PlatformNotificationDelivery }>(`/api/platform/core-network/notification-deliveries/${deliveryId}/retry`, { reason });
    return response.delivery;
  },
  listPortalSessions: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformPortalToken>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformPortalToken> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/portal-sessions', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  revokePortalToken: async (tokenId: number | string, reason: string) => {
    const response = await post<{ success: true; token: PlatformPortalToken }>(`/api/platform/core-network/portal-tokens/${tokenId}/revoke`, { reason });
    return response.token;
  },
  sendTalentPasswordReset: async (memberId: number | string, data: TalentPasswordResetInput): Promise<TalentPasswordManagementResult> => {
    const response = await post<PlatformSuccessEnvelope<TalentPasswordManagementResult>>(`/api/platform/core-network/members/${memberId}/send-password-reset`, data);
    return response;
  },
  generateTalentTemporaryPassword: async (memberId: number | string, data: TalentTemporaryPasswordInput): Promise<TalentTemporaryPasswordResult> => {
    const response = await post<PlatformSuccessEnvelope<TalentTemporaryPasswordResult>>(`/api/platform/core-network/members/${memberId}/generate-temporary-password`, data);
    return response;
  },
  setTalentTemporaryPassword: async (memberId: number | string, data: SetTalentTemporaryPasswordInput): Promise<TalentPasswordManagementResult> => {
    const response = await post<PlatformSuccessEnvelope<TalentPasswordManagementResult>>(`/api/platform/core-network/members/${memberId}/set-temporary-password`, data);
    return response;
  },
  revokeAllMemberSessions: async (memberId: number | string, reason: string): Promise<RevokeAllSessionsResult> => {
    const response = await post<PlatformSuccessEnvelope<RevokeAllSessionsResult>>(`/api/platform/core-network/members/${memberId}/revoke-all-sessions`, { reason });
    return { memberId: response.memberId, revokedSessionCount: response.revokedSessionCount };
  },
  listSecurityEvents: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformAuditEvent>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformAuditEvent> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/security-events', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  listAuditEvents: async (filters: Record<string, unknown>, options: PlatformRequestOptions = {}): Promise<PaginatedResult<PlatformAuditEvent>> => {
    const response = await request<PlatformSuccessEnvelope<PaginatedResult<PlatformAuditEvent> & Record<string, unknown>>>({ method: 'get', url: '/api/platform/core-network/audit-events', params: Object.fromEntries(serializePlatformFilters(filters)), signal: options.signal });
    return unwrapPaginatedPlatformEnvelope(response);
  },
  getPlatformSettings: async (options: PlatformRequestOptions = {}) => {
    const response = await request<PlatformSuccessEnvelope<{ settings: PlatformSettings }>>({ method: 'get', url: '/api/platform/core-network/platform-settings', signal: options.signal });
    return unwrapPlatformEnvelope(response, 'settings') as PlatformSettings;
  },
  updatePlatformSettings: async (data: PlatformSettings & { reason: string }) => {
    const response = await patch<{ success: true; settings: PlatformSettings }>('/api/platform/core-network/platform-settings', data);
    return response.settings;
  },
};
